// Per-vertical content registry — builder (Sprint 2: vertical relevance).
//
// Builds one typed `Vertical` per industry by CONSOLIDATING the existing
// per-vertical sources. This file owns the wiring; the data still lives in
// its original home (industries-detail, widget-galleries, demo-products,
// case-studies). Nothing is duplicated or invented here.
//
// A template should import `getVertical(slug)` (or `verticals`) and read
// media/products/proof/cta FROM it, so each surface stays on-topic.

import { industries, type IndustryDetail } from '@/lib/copy/industries-detail'
import { galleryDemoPostsFor } from '@/lib/copy/widget-galleries/demo-bridge'
import { demoPostsFor, type DemoPost, type DemoProduct } from '@/lib/copy/demo-products'
import type { Vertical, ProofPoint, VerticalMedia, VerticalCaseStudy, VerticalCta } from './types'
import { VERTICAL_CTA, VERTICAL_HERO_TIGHTEN, VERTICAL_PROOF } from './copy'

// Real, published, attributable case studies pinned per vertical. These come
// from idukki.io/case-studies/* with public covers — the SAME source the
// industry page already trusts as "real" (vs the illustrative registry). A
// vertical with no published study here gets `caseStudy: null`; we never
// fabricate one to fill the slot.
const REAL_CASE_BY_SLUG: Record<string, VerticalCaseStudy> = {
  furniture: {
    slug: 'leroy-merlin',
    brand: 'Leroy Merlin',
    vertical: 'Furniture',
    stat: '$700K+ added to carts',
    headline: "How Leroy Merlin added over $700K into its carts with Idukki's UGC widgets",
    cover: '/brand/page-images/ecb0a98ecd25.jpg',
  },
  'fashion-and-apparels': {
    slug: 'theater',
    brand: 'Theater',
    vertical: 'Fashion',
    stat: '+20% conversion',
    headline: 'How a fast-growing fashion brand lifted conversion 20% with real customer content',
    cover: '/brand/page-images/3d0fcfa3bf05.jpg',
  },
}

// Default cobalt CTA when a vertical hasn't authored a tightened label set.
// Outcome-verb + vertical-object is filled from the vertical name.
function defaultCta(i: IndustryDetail): VerticalCta {
  const object = i.name.split('+')[0].trim().toLowerCase()
  return {
    primaryLabel: `Build your ${object} wall`,
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  }
}

// Stats → proofPoints. industries-detail `stats` are REAL (consolidated
// public-source ranges or flagged composites). We take up to three so the
// hero strip reads as a confident row, never a crowded grid.
function buildProofPoints(i: IndustryDetail): ProofPoint[] {
  const override = VERTICAL_PROOF[i.slug]
  if (override) return override.slice(0, 3)
  return (i.stats || []).slice(0, 3).map((s) => ({ stat: s.value, label: s.label }))
}

// Vertical-matched media. Prefer the vertical's own gallery-JSON assets (its
// reels + tagged products); fall back to the demo-products table keyed to the
// slug. Both are category-pinned, so neither path borrows another vertical's
// imagery.
function buildMedia(i: IndustryDetail): VerticalMedia {
  const galleryDemo = galleryDemoPostsFor(i.slug)
  const hasGallery = galleryDemo.posts.length > 0

  const reels: DemoPost[] = hasGallery ? galleryDemo.posts : demoPostsFor(i.slug)
  const sampleProducts: Record<string, DemoProduct> | undefined = hasGallery
    ? galleryDemo.products
    : undefined

  // The gallery (grid surface) reuses the same vertical-matched pool; the
  // template can show a different slice. We keep both populated so a
  // consuming surface never has to reach outside the vertical.
  const gallery: DemoPost[] = reels

  return {
    reels,
    gallery,
    sampleProducts,
    // Real per-vertical hero image when imported; else undefined →
    // template renders a neutral placeholder (no cross-vertical borrow).
    // TODO: real per-vertical hero assets are only present for a subset of
    // verticals (fashion, beauty, jewellery, home). The rest fall through
    // to a neutral placeholder until their liveAssets.hero is imported.
    heroImage: i.liveAssets?.hero,
    heroVideo: i.liveAssets?.heroVideo,
  }
}

function buildVertical(i: IndustryDetail): Vertical {
  const tightened = VERTICAL_HERO_TIGHTEN[i.slug]
  const hero = {
    eyebrow: i.hero.eyebrow,
    headline: tightened?.headline ?? i.hero.headline,
    subhead: tightened?.subhead ?? i.hero.sub,
  }

  const faq = i.faqs ?? []

  return {
    slug: i.slug,
    name: i.name,
    hero,
    proofPoints: buildProofPoints(i),
    media: buildMedia(i),
    caseStudy: REAL_CASE_BY_SLUG[i.slug] ?? null,
    cta: VERTICAL_CTA[i.slug] ?? defaultCta(i),
    seo: {
      title: `${i.name}. UGC playbook + benchmarks`,
      h1: hero.headline,
      intent: `${i.name.toLowerCase()} brands looking to turn customer UGC into shoppable, on-store proof`,
      answerSummary: hero.subhead,
      faq,
    },
  }
}

export const verticals: Vertical[] = industries.map(buildVertical)

const BY_SLUG = new Map<string, Vertical>()
for (const i of industries) {
  const v = buildVertical(i)
  BY_SLUG.set(i.slug, v)
  for (const alias of i.aliases ?? []) BY_SLUG.set(alias, v)
}

/** Resolve a vertical by slug or legacy alias. Returns undefined if unknown. */
export function getVertical(slug: string): Vertical | undefined {
  return BY_SLUG.get(slug)
}

export type { Vertical } from './types'
